export function initHelpPopovers() {
    const buttons = Array.from(document.querySelectorAll('.help-btn'));
    const popovers = Array.from(document.querySelectorAll('.help-popover'));
    if (!buttons.length || !popovers.length) return;
    const backdrop = document.getElementById('helpBackdrop');
    const supportsHover = window.matchMedia('(hover: hover)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let hoverCloseTimer = null;
    let activePopover = null;
    let activeButton = null;
    let activeParent = null;
    let activeNext = null;
    let activeAnchor = null;
    let loopTimer = null;
    let activeLoopRoot = null;
    let activeLoopImage = null;
    let activeLoopValue = null;
    let activeLoopFrames = [];
    let activeLoopIndex = 0;
    let activeLoopFrameToken = 0;
    let activeLoopNormalMs = 1250;
    let activeLoopEdgeMs = 1250;
    let activeLoopLabelPrefix = 'Value';
    let activeLoopLabelSuffix = '';

    const positionPopover = () => {
        if (!activePopover || !activeButton) return;
        const margin = window.innerWidth <= 768 ? 20 : 12;
        const gap = 10;
        const btnRect = activeButton.getBoundingClientRect();
        const anchorRect = activeAnchor
            ? activeAnchor.getBoundingClientRect()
            : btnRect;

        const maxWidth = Math.max(160, window.innerWidth - (margin * 2));
        const anchorWidth = Math.max(0, Number(anchorRect.width) || 0);
        const targetWidth = Math.min(anchorWidth || 280, maxWidth);
        activePopover.style.width = `${Math.round(targetWidth)}px`;
        activePopover.style.maxWidth = `${Math.round(maxWidth)}px`;

        const popRect = activePopover.getBoundingClientRect();
        const topIfAbove = btnRect.top - popRect.height - gap;
        let top;
        if (topIfAbove >= margin) {
            top = topIfAbove;
        } else {
            top = btnRect.bottom + gap;
        }

        if (top + popRect.height > window.innerHeight - margin) {
            top = Math.max(margin, window.innerHeight - margin - popRect.height);
        }

        let left = anchorRect.right - popRect.width;
        if (left < margin) left = margin;
        if (left + popRect.width > window.innerWidth - margin) {
            left = window.innerWidth - margin - popRect.width;
        }
        activePopover.style.top = `${top}px`;
        activePopover.style.left = `${left}px`;
    };

    const clearLoopTimer = () => {
        if (!loopTimer) return;
        window.clearTimeout(loopTimer);
        loopTimer = null;
    };

    const stopActiveLoop = () => {
        clearLoopTimer();
        activeLoopRoot = null;
        activeLoopImage = null;
        activeLoopValue = null;
        activeLoopFrames = [];
        activeLoopIndex = 0;
        activeLoopFrameToken = 0;
        activeLoopNormalMs = 1250;
        activeLoopEdgeMs = 1250;
        activeLoopLabelPrefix = 'Value';
        activeLoopLabelSuffix = '';
    };

    const formatLoopValueText = (value) => {
        const suffix = activeLoopLabelSuffix || '';
        if (activeLoopLabelPrefix) return `${activeLoopLabelPrefix}: ${value}${suffix}`;
        return `${value}${suffix}`;
    };

    const scheduleLoopTick = () => {
        if (prefersReducedMotion) return;
        if (!activeLoopFrames.length || !activeLoopImage) return;
        const isEdge = activeLoopIndex === 0 || activeLoopIndex === activeLoopFrames.length - 1;
        const delayMs = isEdge ? activeLoopEdgeMs : activeLoopNormalMs;
        clearLoopTimer();
        loopTimer = window.setTimeout(() => {
            if (!activeLoopFrames.length || !activeLoopImage) return;
            const nextIndex = (activeLoopIndex + 1) % activeLoopFrames.length;
            setLoopFrame(nextIndex);
            scheduleLoopTick();
        }, delayMs);
    };

    const loopPreviewUnavailable = () => {
        clearLoopTimer();
        if (!activeLoopRoot || !activeLoopValue) return;
        activeLoopRoot.classList.add('is-unavailable');
        activeLoopValue.textContent = activeLoopLabelPrefix
            ? `${activeLoopLabelPrefix}: preview unavailable`
            : 'preview unavailable';
    };

    const setLoopFrame = (index) => {
        if (!activeLoopFrames.length || !activeLoopImage || !activeLoopValue) return;
        const safeIndex = Math.max(0, Math.min(index, activeLoopFrames.length - 1));
        const frame = activeLoopFrames[safeIndex];
        if (!frame) return;
        activeLoopIndex = safeIndex;
        const valueText = formatLoopValueText(frame.value);
        const altPrefix = activeLoopLabelPrefix ? `${activeLoopLabelPrefix} ` : '';
        const altSuffix = activeLoopLabelSuffix || '';
        const altText = `${altPrefix}${frame.value}${altSuffix} preview`;
        const frameToken = activeLoopFrameToken + 1;
        activeLoopFrameToken = frameToken;
        activeLoopImage.addEventListener('load', () => {
            if (frameToken !== activeLoopFrameToken) return;
            activeLoopValue.textContent = valueText;
            activeLoopImage.alt = altText;
            positionPopover();
        }, { once: true });
        activeLoopImage.addEventListener('error', () => {
            if (frameToken !== activeLoopFrameToken) return;
            loopPreviewUnavailable();
        }, { once: true });
        activeLoopImage.src = frame.src;
    };

    const startPopoverLoop = (popover) => {
        if (!popover) return;
        const root = popover.querySelector('[data-help-loop]');
        if (!root) return;
        const imageEl = root.querySelector('.help-loop-image');
        const valueEl = root.querySelector('.help-loop-value');
        if (!imageEl || !valueEl) return;
        const folder = String(root.getAttribute('data-help-loop') || '')
            .trim()
            .replace(/^\/+|\/+$/g, '');
        const rawValues = String(root.getAttribute('data-help-values') || '');
        const values = rawValues
            .split(',')
            .map((value) => String(value || '').trim())
            .filter(Boolean);
        if (!folder || !values.length) return;
        const rawExt = String(root.getAttribute('data-help-ext') || 'avif')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');
        const ext = rawExt || 'avif';

        const parseDelay = (raw, fallback) => {
            const value = Number(raw);
            if (!Number.isFinite(value) || value <= 0) return fallback;
            return Math.round(value);
        };
        const pingPongRaw = String(root.getAttribute('data-help-pingpong') || '').trim().toLowerCase();
        const pingPongEnabled = pingPongRaw === '1' || pingPongRaw === 'true' || pingPongRaw === 'yes' || pingPongRaw === 'on';
        const sequence = (pingPongEnabled && values.length > 1)
            ? values.concat(values.slice(1, -1).reverse())
            : values;

        stopActiveLoop();
        root.classList.remove('is-unavailable');

        activeLoopRoot = root;
        activeLoopImage = imageEl;
        activeLoopValue = valueEl;
        const rawPrefixAttr = root.getAttribute('data-help-label-prefix');
        activeLoopLabelPrefix = rawPrefixAttr == null ? 'Value' : String(rawPrefixAttr).trim();
        const rawSuffixAttr = root.getAttribute('data-help-label-suffix');
        activeLoopLabelSuffix = rawSuffixAttr == null ? '' : String(rawSuffixAttr).trim();
        activeLoopNormalMs = parseDelay(root.getAttribute('data-help-normal-ms'), 1250);
        activeLoopEdgeMs = parseDelay(root.getAttribute('data-help-edge-ms'), 1250);
        activeLoopFrames = sequence.map((value) => ({
            value,
            src: `/static/help/${folder}/${encodeURIComponent(value)}.${ext}`,
        }));

        setLoopFrame(0);
        scheduleLoopTick();
    };

    const closeAll = () => {
        stopActiveLoop();
        if (hoverCloseTimer) {
            window.clearTimeout(hoverCloseTimer);
            hoverCloseTimer = null;
        }
        popovers.forEach((popover) => {
            popover.classList.remove('is-open');
            popover.classList.remove('is-hover');
            popover.setAttribute('aria-hidden', 'true');
        });
        buttons.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
        if (backdrop) {
            backdrop.classList.remove('is-active');
            backdrop.setAttribute('aria-hidden', 'true');
        }
        document.body.classList.remove('help-blur-active');
        document.body.classList.remove('help-blur-hover');
        if (activePopover && activeParent) {
            activePopover.style.position = '';
            activePopover.style.top = '';
            activePopover.style.left = '';
            activePopover.style.right = '';
            activePopover.style.bottom = '';
            activePopover.style.width = '';
            activePopover.style.maxWidth = '';
            if (activeNext && activeNext.parentElement === activeParent) {
                activeParent.insertBefore(activePopover, activeNext);
            } else {
                activeParent.appendChild(activePopover);
            }
        }
        activePopover = null;
        activeButton = null;
        activeParent = null;
        activeNext = null;
        activeAnchor = null;
        window.removeEventListener('resize', positionPopover);
        window.removeEventListener('scroll', positionPopover, true);
    };

    const loadDeferredImages = (root) => {
        if (!root || typeof root.querySelectorAll !== 'function') return;
        const imgs = Array.from(root.querySelectorAll('img[data-src]'));
        let repositionScheduled = false;
        const scheduleReposition = () => {
            if (repositionScheduled) return;
            repositionScheduled = true;
            const run = () => {
                repositionScheduled = false;
                positionPopover();
            };
            if (typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(run);
            } else {
                window.setTimeout(run, 0);
            }
        };
        imgs.forEach((img) => {
            const current = img.getAttribute('src');
            if (current) return;
            const src = img.getAttribute('data-src');
            if (!src) return;
            img.addEventListener('load', scheduleReposition, { once: true });
            img.addEventListener('error', scheduleReposition, { once: true });
            img.setAttribute('src', src);
        });
    };

    const openPopover = (btn, showBackdrop = true) => {
        const key = btn.getAttribute('data-help');
        if (!key) return;
        const popover = document.getElementById(`help-${key}`);
        if (!popover) return;
        closeAll();
        activePopover = popover;
        activeButton = btn;
        activeParent = popover.parentElement;
        activeNext = popover.nextElementSibling;
        activeAnchor = btn.closest('.setting-group')
            || btn.closest('.setting-row')
            || btn.closest('.toggle-pair-item')
            || btn.closest('.setting-label-row')
            || btn;
        loadDeferredImages(popover);
        document.body.appendChild(popover);
        popover.classList.add('is-open');
        popover.classList.toggle('is-hover', !showBackdrop);
        popover.setAttribute('aria-hidden', 'false');
        popover.style.position = 'fixed';
        popover.style.right = 'auto';
        popover.style.bottom = 'auto';
        popover.style.top = '0px';
        popover.style.left = '0px';
        positionPopover();
        startPopoverLoop(popover);
        btn.setAttribute('aria-expanded', 'true');
        if (backdrop && showBackdrop) {
            backdrop.classList.add('is-active');
            backdrop.setAttribute('aria-hidden', 'false');
        }
        if (showBackdrop) {
            document.body.classList.add('help-blur-active');
        } else {
            document.body.classList.add('help-blur-hover');
        }
        window.addEventListener('resize', positionPopover);
        window.addEventListener('scroll', positionPopover, true);
    };

    const togglePopover = (btn) => {
        const key = btn.getAttribute('data-help');
        if (!key) return;
        const popover = document.getElementById(`help-${key}`);
        if (!popover) return;
        const isOpen = popover.classList.contains('is-open');
        if (isOpen) {
            closeAll();
        } else {
            openPopover(btn, true);
        }
    };

    const scheduleHoverClose = () => {
        if (!supportsHover) return;
        if (hoverCloseTimer) window.clearTimeout(hoverCloseTimer);
        hoverCloseTimer = window.setTimeout(() => closeAll(), 140);
    };

    buttons.forEach((btn) => {
        btn.addEventListener('click', (event) => {
            event.stopPropagation();
            togglePopover(btn);
        });
        if (supportsHover) {
            btn.addEventListener('mouseenter', () => openPopover(btn, false));
            btn.addEventListener('mouseleave', scheduleHoverClose);
        }
    });

    popovers.forEach((popover) => {
        popover.addEventListener('click', (event) => {
            event.stopPropagation();
        });
        if (supportsHover) {
            popover.addEventListener('mouseenter', () => {
                if (hoverCloseTimer) {
                    window.clearTimeout(hoverCloseTimer);
                    hoverCloseTimer = null;
                }
            });
            popover.addEventListener('mouseleave', scheduleHoverClose);
        }
    });

    if (backdrop) {
        backdrop.addEventListener('click', () => closeAll());
    }

    document.addEventListener('click', () => closeAll());
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeAll();
    });
}
